import { useCallback } from 'react';
import { useService } from '../useService';
import { useAppDispatch } from '../useReduxToolkit';
import {
  useProviderViewCallParticipants,
  type ProviderViewCallParticipant,
} from '../useProviderViewCallParticipants';
import { getLogger } from '../../libs/logger';
import { usePhotoCaptureEvents } from '../../components/call/photoCapture/usePhotoCaptureEvents';
import { toast } from '@doxyme/ui/components';
import { useIntl } from '@doxyme/locale';

const logger = getLogger('usePhotoCapture');

type UsePhotoCaptureReturnType = {
  startPhotoCapture: () => void;
};

export const usePhotoCapture = (): UsePhotoCaptureReturnType => {
  const dispatch = useAppDispatch();
  const photoCapture = useService('photoCapture');
  const { realtimeCallParticipants } = useProviderViewCallParticipants();
  const { sendPhotoCaptureStartedEvent } = usePhotoCaptureEvents();
  const { formatMessage } = useIntl();

  const getParticipant = (): ProviderViewCallParticipant | undefined => {
    if (realtimeCallParticipants.length === 0) {
      logger.error('No call participants for photo capture');
      return;
    }

    if (realtimeCallParticipants.length > 1) {
      toast.error({
        title: formatMessage({
          id: 'photoCaptureModal.notification.groupCallNotAvailable',
        }),
      });

      return;
    }

    const participant = realtimeCallParticipants[0];

    if (!participant) {
      logger.error('participant is undefined');
      return;
    }

    return participant;
  };

  const startPhotoCapture = useCallback((): void => {
    if (photoCapture.getCurrentRequest()) {
      logger.warn('Ignoring start photo capture, request already in progress');
      return;
    }

    const participant = getParticipant();

    if (!participant) {
      return;
    }

    if (!participant.hasVideo) {
      toast.error({
        title: formatMessage({
          id: 'photoCaptureModal.notification.patientCameraOffMessage',
        }),
      });

      return;
    }

    if (participant.id) {
      sendPhotoCaptureStartedEvent();
      void photoCapture.ask(participant.id);
    }
  }, [realtimeCallParticipants, photoCapture, dispatch]);

  return { startPhotoCapture };
};
